import { useEffect, useMemo, useState } from 'react'
import { X, Loader2, Trash2, Plus, Check, Pencil, RotateCcw, Wallet, Lock } from 'lucide-react'
import { apiGet, postJson } from '../../lib/api.js'
import { fullName, fmtMoney, relabelBranch, indexBranches } from '../../lib/employee.js'
import {
  RECURRING_TYPES, recurringTypeTone, recurringProgress, collectedFor, indexCollected, newRecurring,
} from '../../lib/recurring.js'
import EmployeePicker from './EmployeePicker.jsx'
import ConfirmDialog from '../ConfirmDialog.jsx'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'
// Its own string, NOT `${FIELD} bg-slate-100 …` — Tailwind resolves that conflict by
// stylesheet order, not attribute order, so FIELD's bg-white/text-slate-800 win and the
// greys do nothing. Same fix as FIELD_RO in EmployeeEditor.jsx.
const FIELD_RO = 'w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-[13.5px] text-slate-500 placeholder:text-slate-400 outline-none cursor-not-allowed'
const LABEL = 'block text-[12px] font-medium text-slate-500 mb-1'

const round = (n) => Math.round(Number(n) || 0)

// A recurring entry rendered as one progress card in the list.
function EntryCard({ entry, collected, onEdit, onDelete, active }) {
  const p = recurringProgress(entry, collected)
  const barTone = p.done ? 'bg-emerald-500' : 'bg-brand-500'
  // An entry with any deducted installment is locked — editing/deleting it would
  // desync payroll history (also enforced server-side, 409).
  const locked = collected > 0
  return (
    <div className={`rounded-xl border px-3.5 py-3 ${active ? 'border-brand-300 ring-2 ring-brand-500/15 bg-brand-50/30' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${recurringTypeTone(entry.type)}`}>{entry.type || 'อื่นๆ'}</span>
        {p.done && <span className="inline-flex items-center gap-0.5 text-[11.5px] font-medium text-emerald-600"><Check size={12} /> ครบแล้ว</span>}
        <div className="ml-auto flex items-center gap-1">
          {locked ? (
            <span title="มีการหักในสลิปเงินเดือนแล้ว แก้ไข/ลบไม่ได้"
              className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400 px-1.5 py-1">
              <Lock size={12} /> ล็อก
            </span>
          ) : (
            <>
              <button onClick={() => onEdit(entry)} className="p-1.5 rounded-lg text-slate-400 hover:text-brand-600 hover:bg-brand-50" title="แก้ไข"><Pencil size={14} /></button>
              <button onClick={() => onDelete(entry)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50" title="ลบ"><Trash2 size={14} /></button>
            </>
          )}
        </div>
      </div>

      {entry.remark && <div className="text-[12.5px] text-slate-500 mt-1.5">{entry.remark}</div>}

      <div className="flex items-baseline justify-between mt-2 text-[13px]">
        <div className="text-slate-600">
          งวดละ <span className="font-semibold text-slate-800 tabular-nums">{fmtMoney(p.amountPer)}</span>
          <span className="text-slate-400"> · รวม {fmtMoney(p.amountTotal)}</span>
        </div>
        <div className="tabular-nums font-medium text-slate-700">{p.collected}/{p.total} งวด</div>
      </div>

      {/* progress bar */}
      <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${barTone} transition-all`} style={{ width: `${p.pct}%` }} />
      </div>
      <div className="flex items-center justify-between mt-1 text-[11.5px] text-slate-400 tabular-nums">
        <span>หักแล้ว {fmtMoney(p.amountPer * p.collected)}</span>
        <span>เหลือ {fmtMoney(p.amountRemaining)} ({p.remaining} งวด)</span>
      </div>
    </div>
  )
}

export default function RecurringEditor({ employeeId: initialId = null, branches = [], onClose, onSaved }) {
  const [employeeId, setEmployeeId] = useState(initialId ? String(initialId) : '')
  const [record, setRecord] = useState(null)      // full Employee record (name + recurring[])
  const [entries, setEntries] = useState([])
  const [collectedMap, setCollectedMap] = useState(new Map()) // uuid → {collected, amount}
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [confirmDel, setConfirmDel] = useState(null) // entry pending delete

  // form — editingUuid null = add-new; a uuid = editing that entry.
  const [editingUuid, setEditingUuid] = useState(null)
  const [form, setForm] = useState(newRecurring())

  const branchName = useMemo(() => indexBranches(branches), [branches])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  function applyRecord(e) {
    setRecord(e)
    setEntries(Array.isArray(e?.recurring) ? e.recurring : [])
  }

  // Load the chosen employee's full record (recurring[] lives on the row).
  useEffect(() => {
    if (!employeeId) { setRecord(null); setEntries([]); setCollectedMap(new Map()); return }
    let alive = true
    setLoading(true); setErr('')
    // Record carries recurring[]; the collected counts (true X of N) come from the
    // salary_em aggregate keyed by recurring_uuid.
    Promise.all([
      apiGet(`/api/hr/employees/${employeeId}`),
      apiGet('/api/hr/employee-recurring-collected', { employee_id: employeeId }).catch(() => ({ items: [] })),
    ])
      .then(([e, coll]) => {
        if (!alive) return
        if (e) applyRecord(e)
        setCollectedMap(indexCollected(Array.isArray(coll) ? coll : (coll?.items || [])))
      })
      .catch((e) => { if (alive) setErr(String(e.message || e)) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [employeeId])

  // Per-installment amount is ALWAYS derived: ยอดรวม ÷ จำนวนงวด. Not user-editable.
  useEffect(() => {
    const total = Number(form.amountTotal) || 0
    const n = Number(form.N) || 0
    setForm((f) => ({ ...f, amount: n > 0 ? round(total / n) : 0 }))
  }, [form.amountTotal, form.N])

  function resetForm() {
    setEditingUuid(null)
    setForm(newRecurring())
  }

  function startEdit(entry) {
    setEditingUuid(entry.uuid || null)
    setForm({
      type: entry.type || 'ค่าฝึกอบรม',
      amountTotal: Number(entry.amountTotal || 0),
      amount: Number(entry.amount || 0),
      N: Number(entry.N || 0),
      remark: entry.remark || '',
    })
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }))

  async function op(type, recurring) {
    setSaving(true); setErr('')
    try {
      const out = await postJson('/api/hr/employee-recurring', { employee_id: Number(employeeId), type, recurring })
      // POST returns the updated Employee; fall back to a reload if it's missing recurring[].
      if (out && Array.isArray(out.recurring)) applyRecord(out)
      else { const e = await apiGet(`/api/hr/employees/${employeeId}`); if (e) applyRecord(e) }
      onSaved?.()
      return true
    } catch (e) {
      // Surface the server's Thai message (e.g. the locked-entry 409) rather than "HTTP 409: {…}".
      const raw = String(e.message || e)
      const brace = raw.indexOf('{')
      let msg = raw
      if (brace >= 0) { try { const o = JSON.parse(raw.slice(brace)); msg = o.message || o.error || raw } catch { /* not json */ } }
      setErr(msg); return false
    }
    finally { setSaving(false) }
  }

  async function submit() {
    const amountTotal = round(form.amountTotal)
    const N = round(form.N)
    const amount = N >= 1 ? round(amountTotal / N) : 0 // derived — ยอดรวม ÷ จำนวนงวด
    if (!form.type) { setErr('เลือกประเภทการหัก'); return }
    if (N < 1) { setErr('จำนวนงวดต้องมากกว่า 0'); return }
    if (amountTotal < 1) { setErr('ยอดรวมต้องมากกว่า 0'); return }
    if (amount < 1) { setErr('ยอดรวมน้อยเกินไปสำหรับจำนวนงวดที่ระบุ'); return }

    if (editingUuid) {
      const original = entries.find((x) => x.uuid === editingUuid)
      if (!original) { setErr('ไม่พบรายการที่จะแก้ไข'); return }
      // Spread the original so uuid + salary_em_id + created_* survive the round-trip
      // (Xano re-appends $input.recurring verbatim on edit).
      const ok = await op('edit', { ...original, type: form.type, amountTotal, amount, N, remark: form.remark.trim() })
      if (ok) resetForm()
    } else {
      const ok = await op('add', { type: form.type, amountTotal, amount, N, remark: form.remark.trim() })
      if (ok) resetForm()
    }
  }

  async function doDelete(entry) {
    setConfirmDel(null)
    const ok = await op('delete', { uuid: entry.uuid })
    if (ok && editingUuid === entry.uuid) resetForm()
  }

  const empName = record ? fullName(record) : ''
  const empBranch = record ? relabelBranch(branchName.get(Number(record.branch_id)) || '') : ''

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-[560px] bg-white shadow-2xl flex flex-col animate-ss-slide-in">
        {/* header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold text-slate-900">รายการหักประจำ</h2>
            {empName && <p className="text-[12.5px] text-slate-500 truncate">{empName}{empBranch ? ` · ${empBranch}` : ''}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* target picker — only when opened without a fixed employee */}
          {!initialId && (
            <div className="mb-4">
              <label className={LABEL}>พนักงาน</label>
              <EmployeePicker value={employeeId} onChange={setEmployeeId} placeholder="เลือกพนักงาน…" className="w-full" />
            </div>
          )}

          {loading ? (
            <div className="py-12 flex items-center justify-center text-slate-400"><Loader2 size={18} className="animate-spin mr-2" /> กำลังโหลด…</div>
          ) : !employeeId ? (
            <p className="py-10 text-center text-[13px] text-slate-400">เลือกพนักงานเพื่อจัดการรายการหักประจำ</p>
          ) : (
            <>
              {/* existing entries */}
              <div className="space-y-2.5 mb-5">
                {entries.length === 0 && <p className="text-[13px] text-slate-400">ยังไม่มีรายการหักประจำ</p>}
                {entries.map((e) => (
                  <EntryCard key={e.uuid || `${e.type}-${e.created_at}`} entry={e} collected={collectedFor(collectedMap, e)} active={editingUuid === e.uuid}
                    onEdit={startEdit} onDelete={(x) => setConfirmDel(x)} />
                ))}
              </div>

              {/* add / edit form */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Wallet size={15} className="text-brand-600" strokeWidth={1.9} />
                  <h3 className="text-[13.5px] font-semibold text-slate-700">{editingUuid ? 'แก้ไขรายการ' : 'เพิ่มรายการหักประจำ'}</h3>
                  {editingUuid && (
                    <button onClick={resetForm} className="ml-auto inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-700">
                      <RotateCcw size={12} /> ยกเลิกแก้ไข
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className={LABEL}>ประเภทการหัก</label>
                    <div className="flex flex-wrap gap-1.5">
                      {RECURRING_TYPES.map((t) => {
                        const on = form.type === t
                        return (
                          <button key={t} type="button" onClick={() => set('type')(t)}
                            className={`px-2.5 py-1 rounded-lg text-[12.5px] font-medium ring-1 transition ${on ? 'bg-brand-50 text-brand-700 ring-brand-200' : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'}`}>
                            {t}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                  <div>
                    <label className={LABEL}>ยอดรวม (บาท)</label>
                    <input type="number" min="0" value={form.amountTotal} onChange={set('amountTotal')} className={FIELD} />
                  </div>
                  <div>
                    <label className={LABEL}>จำนวนงวด</label>
                    <input type="number" min="1" value={form.N} onChange={set('N')} className={FIELD} />
                  </div>
                  <div>
                    <label className={LABEL}>หักงวดละ (บาท)</label>
                    <input type="number" value={form.amount} readOnly tabIndex={-1}
                      className={FIELD_RO} />
                  </div>
                  <div className="flex items-end pb-0.5">
                    <p className="text-[11.5px] text-slate-400 leading-snug">คำนวณอัตโนมัติจากยอดรวม ÷ จำนวนงวด</p>
                  </div>
                  <div className="col-span-2">
                    <label className={LABEL}>หมายเหตุ</label>
                    <input value={form.remark} onChange={set('remark')} placeholder="เช่น ค่าอบรมคอร์ส…" className={FIELD} />
                  </div>
                </div>

                <button onClick={submit} disabled={saving}
                  className="mt-3.5 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : (editingUuid ? <Check size={15} /> : <Plus size={15} />)}
                  {editingUuid ? 'บันทึกการแก้ไข' : 'เพิ่มรายการ'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* footer */}
        <div className="border-t border-slate-100 px-6 py-3.5 flex items-center justify-between gap-3">
          <span className="text-[12.5px] text-rose-500 truncate">{err}</span>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-slate-100 shrink-0">ปิด</button>
        </div>
      </div>

      {confirmDel && (
        <ConfirmDialog
          title="ลบรายการหักประจำ"
          message={`ลบ “${confirmDel.type}” (งวดละ ${fmtMoney(Number(confirmDel.amount || 0))}) ออกจากพนักงานคนนี้?`}
          confirmLabel="ลบ"
          tone="danger"
          onConfirm={() => doDelete(confirmDel)}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </div>
  )
}
